import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, AlertCircle, Calendar, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CALENDLY_URL = "https://calendly.com/s-jurczeszyn59/30min";

const PRSNLSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus('error');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
        if (!response.ok) throw new Error('Failed to check status');

        const data = await response.json();
        setPaymentData(data);

        if (data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (data.status === 'expired') {
          setStatus('error');
          return;
        }

        // Continue polling
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error('Error checking payment:', error);
        if (attempts >= maxAttempts - 1) {
          setStatus('error');
        } else {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <Loader2 className="w-16 h-16 text-[#F05A1A] animate-spin mx-auto mb-6" />
          <h1 className="text-white text-2xl font-bold mb-2">Vérification du paiement...</h1>
          <p className="text-gray-400">Merci de patienter quelques secondes</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-white text-2xl font-bold mb-4">Un problème est survenu</h1>
          <p className="text-gray-400 mb-8">
            Le paiement n'a pas pu être vérifié. Si vous avez été débité, contactez-nous.
          </p>
          <Link
            to="/prsnl"
            className="inline-block bg-[#F05A1A] text-white font-bold px-8 py-4 rounded-xl"
          >
            Retour à la page de vente
          </Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#111111] flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center max-w-lg w-full"
      >
        {/* Success Icon */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", delay: 0.2 }}
          className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-8"
        >
          <Check className="w-12 h-12 text-white" />
        </motion.div>

        {/* Title */}
        <h1 className="font-bebas text-4xl sm:text-5xl text-white mb-4">
          BIENVENUE DANS
          <span className="block text-[#F05A1A]">L'ACCOMPAGNEMENT PRSNL</span>
        </h1>

        <p className="text-gray-400 text-lg mb-8">
          Votre paiement a été confirmé. Vous allez recevoir un email avec toutes les informations pour démarrer.
        </p>

        {/* Payment Info */}
        <div className="bg-[#1E1E1E] rounded-2xl p-6 mb-8 text-left">
          <h2 className="text-white font-bold mb-4">Récapitulatif</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Programme</span>
              <span className="text-white">PRSNL - Accompagnement 90 jours</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Montant</span>
              <span className="text-white">997€</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Statut</span>
              <span className="text-green-500 font-semibold">Payé ✓</span>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="bg-[#1E1E1E] rounded-2xl p-6 mb-8 text-left">
          <h2 className="text-white font-bold mb-4">Prochaines étapes</h2>
          <ul className="space-y-4">
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#F05A1A] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Mail className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Email de bienvenue</p>
                <p className="text-gray-500 text-sm">Vous allez recevoir un email avec toutes les informations</p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-8 h-8 bg-[#F05A1A] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold">Premier appel</p>
                <p className="text-gray-500 text-sm">Réservez votre premier appel de coaching</p>
              </div>
            </li>
          </ul>
        </div>

        {/* CTA */}
        <a
          href={CALENDLY_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full bg-[#F05A1A] hover:bg-[#D94D0F] text-white font-bold py-5 rounded-xl text-center transition-all shadow-[0_8px_32px_rgba(240,90,26,0.4)] mb-4"
        >
          Réserver mon premier appel →
        </a>

        <Link
          to="/"
          className="text-gray-500 hover:text-white text-sm transition-colors"
        >
          Retour à l'accueil
        </Link>
      </motion.div>
    </div>
  );
};

export default PRSNLSuccessPage;
